import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { stripeApi } from '@client/api/waterlily';
import { postStripeSubscriptionCheckoutSessionUrlResponse } from '@client/model/stripeSubscribeSessionModel';
import { useToast } from '@/components/hooks/use-toast';

type PlanType = 'trial' | 'monthly' | 'annual';

interface UseStripeCheckoutSessionOptions {
  advisorId: string;
  checkoutPath?: string;
  skipNavigation?: boolean;
}

export function useStripeCheckoutSession({
  advisorId,
  checkoutPath,
  skipNavigation = false,
}: UseStripeCheckoutSessionOptions) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { errorToast } = useToast();

  const mutation = useMutation({
    mutationFn: (params: { planType: PlanType; priceId?: string; returnPath?: string }) =>
      stripeApi.createCheckoutSession({
        advisorId,
        planType: params.planType,
        priceId: params.priceId,
        returnPath: params.returnPath,
        uiMode: 'embedded',
      }),
    onSuccess: (data, variables) => {
      dispatch(
        postStripeSubscriptionCheckoutSessionUrlResponse({ clientSecret: data.clientSecret }),
      );
      if (!skipNavigation) {
        const planParam = variables.planType === 'trial' ? 'free' : variables.planType;
        const path = checkoutPath ?? `/advisors/${advisorId}/onboarding/activate-seat`;
        navigate(`${path}?plan=${planParam}`);
      }
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.';
      errorToast('Checkout Error', errorMessage, 8000);
    },
  });

  return {
    createCheckoutSession: mutation.mutate,
    isPending: mutation.isPending,
    pendingPlanType: mutation.isPending ? mutation.variables?.planType : null,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    clientSecret: mutation.data?.clientSecret ?? null,
    reset: mutation.reset,
  };
}
